import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { Checkbox } from '../ui/Checkbox';
import { WizardStepHeader } from '../wizard';
import { categoryMeta } from '../accounts/assetCategories';
import { useAllocationSetupQuery } from '../../features/emergencyFund/queries';
import pt from '../../locales/pt';
import { parseAmount } from '../../lib/emergencyFundDisplayUtils';
import { formatEUR } from '../../utils/money';
import { cn } from '../../utils/cn';

function parseBalance(value) {
  return parseAmount(value);
}

function clampAmount(raw, max) {
  const n = parseFloat(raw.replace(',', '.'));
  if (isNaN(n) || n < 0) return 0;
  return Math.min(n, max);
}

export const EmergencyFundAssetPickStep = forwardRef(function EmergencyFundAssetPickStep(
  { bulkSave, targetEur = 0 },
  ref,
) {
  const { data: setup, isLoading } = useAllocationSetupQuery();
  const copy = pt.emergencyFund.wizard.allocations;
  // Map<accountId, amountStr> — only selected accounts appear
  const [allocations, setAllocations] = useState(new Map());
  const inputRefs = useRef({});

  useEffect(() => {
    if (!setup?.accounts) return;
    setAllocations((prev) => {
      const next = new Map(prev);
      setup.accounts.forEach((row) => {
        const id = String(row.account_id);
        const base = parseBalance(row.base_amount_eur);
        const ef = parseBalance(row.ef_amount_eur);
        const existingAmount = base + ef;
        if (existingAmount > 0 && !next.has(id)) {
          next.set(id, existingAmount.toFixed(2));
        }
      });
      return next;
    });
  }, [setup]);

  const selectedTotal = useMemo(() => {
    if (!setup?.accounts) return 0;
    let sum = 0;
    setup.accounts.forEach((row) => {
      const id = String(row.account_id);
      if (!allocations.has(id)) return;
      const balance = parseBalance(row.balance_eur);
      sum += clampAmount(allocations.get(id) ?? '', balance);
    });
    return sum;
  }, [setup, allocations]);

  const target = parseAmount(targetEur);
  const pct = target > 0 ? Math.min(100, Math.round((selectedTotal / target) * 100)) : 0;

  useImperativeHandle(
    ref,
    () => ({
      getSelectedTotal: () => selectedTotal,
      async save() {
        if (!setup?.accounts?.length) return;
        const items = [];
        setup.accounts.forEach((row) => {
          const id = String(row.account_id);
          const balance = parseBalance(row.balance_eur);
          const amount = allocations.has(id)
            ? clampAmount(allocations.get(id) ?? '', balance)
            : 0;
          items.push({ account_id: row.account_id, role: 'base_fund', amount_eur: '0.00' });
          items.push({
            account_id: row.account_id,
            role: 'emergency_fund',
            amount_eur: amount > 0 ? amount.toFixed(2) : '0.00',
          });
        });
        await bulkSave.mutateAsync(items);
      },
    }),
    [setup, allocations, bulkSave, selectedTotal],
  );

  const toggle = (accountId, balance) => {
    const id = String(accountId);
    setAllocations((prev) => {
      const next = new Map(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.set(id, balance.toFixed(2));
        // Focus the input after render
        setTimeout(() => inputRefs.current[id]?.focus(), 50);
      }
      return next;
    });
  };

  const setAmount = (accountId, value) => {
    const id = String(accountId);
    setAllocations((prev) => {
      const next = new Map(prev);
      next.set(id, value);
      return next;
    });
  };

  if (isLoading || !setup) {
    return <p className="text-sm text-muted-foreground">{pt.common.loading}</p>;
  }

  if (!setup.accounts.length) {
    return <p className="text-sm text-muted-foreground">{copy.noAccounts}</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      <WizardStepHeader
        eyebrow={copy.eyebrow}
        title={copy.title}
        subtitle={copy.hint}
      />

      <div className="rounded-[14px] border border-sage-100 bg-sage-50 px-4 py-3.5 dark:border-sage-800 dark:bg-sage-900/40">
        <p className="text-xs font-semibold text-muted-foreground">{copy.sumLabel}</p>
        <p className="mt-0.5 font-display text-2xl font-bold text-sage-700 dark:text-sage-300">
          {formatEUR(selectedTotal)}
        </p>
        {target > 0 ? (
          <p className="mt-1 text-[12px] text-muted-foreground">
            {copy.ofTarget.replace('{pct}', String(pct)).replace('{target}', formatEUR(target))}
          </p>
        ) : null}
      </div>

      <div className="flex flex-col gap-2.5">
        {setup.accounts.map((row) => {
          const Icon = categoryMeta(row.category).icon;
          const id = String(row.account_id);
          const selected = allocations.has(id);
          const balance = parseBalance(row.balance_eur);
          const amountStr = allocations.get(id) ?? '';
          const amountNum = clampAmount(amountStr, balance);
          const exceedsBalance = parseFloat(amountStr.replace(',', '.')) > balance;

          return (
            <div
              key={row.account_id}
              className={cn(
                'overflow-hidden rounded-[14px] border-[1.5px] transition',
                selected
                  ? 'border-sage-600 bg-sage-50 dark:border-sage-500 dark:bg-sage-900/40'
                  : 'border-border bg-card',
              )}
            >
              {/* Account row — click to toggle */}
              <button
                type="button"
                onClick={() => toggle(row.account_id, balance)}
                className="flex w-full items-center gap-3 px-3.5 py-3 text-left"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-sage-100 text-sage-700 dark:bg-sage-900 dark:text-sage-300">
                  <Icon className="h-[18px] w-[18px]" aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-foreground">{row.account_name}</span>
                  <span className="block text-xs text-muted-foreground">
                    {copy.available.replace('{amount}', formatEUR(balance))}
                  </span>
                </span>
                <Checkbox checked={selected} tabIndex={-1} aria-hidden className="pointer-events-none" />
              </button>

              {/* Amount input — only when selected */}
              {selected ? (
                <div
                  className="border-t border-sage-200 px-3.5 pb-3 pt-2.5 dark:border-sage-700"
                  onClick={(e) => e.stopPropagation()}
                >
                  <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-sage-700 dark:text-sage-300">
                    {copy.amountLabel}
                  </label>
                  <div className="relative flex items-center">
                    <span className="pointer-events-none absolute left-3 text-sm font-semibold text-muted-foreground">
                      €
                    </span>
                    <input
                      ref={(el) => { inputRefs.current[id] = el; }}
                      type="number"
                      inputMode="decimal"
                      min="0"
                      max={balance}
                      step="0.01"
                      value={amountStr}
                      placeholder={copy.amountPlaceholder}
                      onChange={(e) => setAmount(row.account_id, e.target.value)}
                      className={cn(
                        'w-full rounded-[10px] border bg-background py-2 pl-7 pr-3 text-sm font-semibold tabular-nums text-foreground outline-none focus:ring-2 focus:ring-sage-500',
                        exceedsBalance
                          ? 'border-destructive focus:ring-destructive'
                          : 'border-border',
                      )}
                    />
                    {balance > 0 && amountNum < balance ? (
                      <button
                        type="button"
                        onClick={() => setAmount(row.account_id, balance.toFixed(2))}
                        className="absolute right-3 text-[11px] font-semibold text-sage-600 hover:underline dark:text-sage-400"
                      >
                        máx
                      </button>
                    ) : null}
                  </div>
                  {exceedsBalance ? (
                    <p className="mt-1 text-[11px] text-destructive">
                      {copy.amountExceedsBalance.replace('{balance}', formatEUR(balance))}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
});

export default EmergencyFundAssetPickStep;
